/**
 * [API 상수] - 엔드포인트 URL, WebSocket URL, 에러 코드 정의
 *
 * API_BASE가 빈 문자열이면 "같은 오리진"을 의미한다.
 * 프로덕션에서는 nginx가 /api/*를 백엔드로 프록시하고,
 * 로컬 개발에서는 Next.js rewrites가 프록시 역할을 한다.
 *
 * 서버 사이드 코드(라우트 핸들러, 서버 컴포넌트)에서는
 * process.env.BACKEND_URL || 'http://localhost:8081'을 직접 사용해야 한다.
 */
export const API_BASE = '';
export const WS_URL = process.env.NEXT_PUBLIC_WS_URL || 'ws://localhost:8081/ws';

export const API_ENDPOINTS = {
  // Auth
  AUTH_LOGIN: '/api/auth/login',
  AUTH_SIGNUP: '/api/auth/signup',
  AUTH_LOGOUT: '/api/auth/logout',
  AUTH_REFRESH: '/api/auth/refresh',
  AUTH_CHECK_USERNAME: '/api/auth/check-username',
  AUTH_CHECK_EMAIL: '/api/auth/check-email',

  // Users
  USER_ME: '/api/users/me',
  USER_AVATAR: '/api/users/me/avatar',
  ['USER_PASSWORD']: '/api/users/me/password',
  /** 등록 사용자 디렉터리 검색(초대 셀렉터 전용). GET ?q=<이름/이메일 2글자↑>. 응답 name·email 2필드·최대 10건. */
  USER_DIRECTORY_SEARCH: '/api/users/directory',
  /** 프로필 메일 발송 설정(사용자별 SMTP 자격증명) — GET 상태 / PUT 업서트 / DELETE 등록해제 */
  USER_MAIL_CREDENTIAL: '/api/users/me/mail-credential',
  /** 메일 발송 설정 연결테스트(SMTP AUTH 핸드셰이크만) */
  USER_MAIL_CREDENTIAL_TEST: '/api/users/me/mail-credential/test',

  // Meetings
  MEETINGS: '/api/meetings',
  MEETING_INSTANT: '/api/meetings/instant',
  MEETING_UPDATE: (id: string) => `/api/meetings/${encodeURIComponent(id)}`,
  /** 회의 초대 명부 조회(편집 모달 프리필, 생성자 또는 관리자 전용). 반환 [{email,name,hookingUrl,userId}]. */
  MEETING_INVITEES: (id: string) => `/api/meetings/${encodeURIComponent(id)}/invitees`,
  /** Ghost 관리자 상태/지정/해제(생성자 또는 관리자 전용). */
  MEETING_GHOST_ADMIN: (id: string) => `/api/meetings/${encodeURIComponent(id)}/ghost-admin`,
  MEETING_BY_ID: (id: string) => `/api/meetings/${encodeURIComponent(id)}`,
  MEETING_BY_CODE: (code: string) => `/api/meetings/by-code/${encodeURIComponent(code)}`,
  MEETING_JOIN: (id: string) => `/api/meetings/${encodeURIComponent(id)}/join`,
  MEETING_LEAVE: (id: string) => `/api/meetings/${encodeURIComponent(id)}/leave`,
  MEETING_END: (id: string) => `/api/meetings/${encodeURIComponent(id)}/end`,
  /** 인증 활성 호스트: in-meeting 대기실 ON/OFF 토글(D-1, 안전 필드 한정). 게스트 경로 GUEST_HOST_WAITING_ROOM의 인증판(JWT 경로). */
  MEETING_WAITING_ROOM: (id: string) => `/api/meetings/${encodeURIComponent(id)}/waiting-room`,
  MEETING_WAITING: (id: string) => `/api/meetings/${encodeURIComponent(id)}/waiting`,
  MEETING_ADMIT: (id: string, participantId: string) =>
    `/api/meetings/${encodeURIComponent(id)}/participants/${encodeURIComponent(participantId)}/admit`,
  MEETING_REJECT: (id: string, participantId: string) =>
    `/api/meetings/${encodeURIComponent(id)}/participants/${encodeURIComponent(participantId)}/reject`,
  /** 공개 회의 정보 조회 (비인증 — 게스트가 회의 숫자 ID/대기실 상태를 해석할 때 사용). POST, body는 {password?} 선택. */
  PUBLIC_MEETING_INFO: (code: string) =>
    `/api/public/meetings/by-code/${encodeURIComponent(code)}/info`,

  // ── 참가자 제어 (인증된 호스트 전용 — JWT Bearer 토큰 인증) ──
  /** 모든 참가자 일괄 음소거 */
  PARTICIPANT_MUTE_ALL: (id: string) =>
    `/api/meetings/${encodeURIComponent(id)}/participants/mute-all`,
  /** 모든 참가자 카메라 일괄 끄기 */
  PARTICIPANT_CAMERA_OFF_ALL: (id: string) =>
    `/api/meetings/${encodeURIComponent(id)}/participants/camera-off-all`,
  /** 특정 참가자의 오디오/비디오 트랙 음소거 */
  PARTICIPANT_MUTE: (id: string, identity: string) =>
    `/api/meetings/${encodeURIComponent(id)}/participants/${encodeURIComponent(identity)}/mute`,
  /** 특정 참가자의 마이크/카메라 강제 켜기 (요청/수락 아님 — 즉시 적용, 인증 호스트 전용) */
  PARTICIPANT_FORCE_ENABLE: (id: string, identity: string) =>
    `/api/meetings/${encodeURIComponent(id)}/participants/${encodeURIComponent(identity)}/force-enable`,
  /** 룸 전원 마이크/카메라 강제 켜기 (인증 호스트 전용) */
  PARTICIPANT_FORCE_ENABLE_ALL: (id: string) =>
    `/api/meetings/${encodeURIComponent(id)}/participants/force-enable-all`,
  /** 미참가자(초대되었으나 미접속) 강제 참가('연결') — AUTO_JOIN 신호(토큰 미포함). 인증 호스트 전용 */
  PARTICIPANT_CONNECT: (id: string) =>
    `/api/meetings/${encodeURIComponent(id)}/participants/connect`,
  /** 미참가자 목록 조회 (초대명부 − 접속자 diff). 인증 호스트 전용 */
  PARTICIPANT_NON_PARTICIPANTS: (id: string) =>
    `/api/meetings/${encodeURIComponent(id)}/participants/non-participants`,
  /** [참가 요청] 후킹URL 보유 미참가 초대자에게 온디맨드 웹훅(meeting.join_request) 발사. 인증 호스트 전용 */
  PARTICIPANT_REQUEST_JOIN: (id: string, inviteeId: string | number) =>
    `/api/meetings/${encodeURIComponent(id)}/participants/invitees/${encodeURIComponent(inviteeId)}/request-join`,
  /**
   * 좌석 사전 배치 재-seed(웹훅 미도달 폴백). 인증 호스트 전용.
   * 본문 없음 — 좌석·예측 identity는 서버가 초대 명부에서 직접 계산한다(클라 계산 불신뢰).
   * 서버가 멱등(이미 `seats`가 있으면 write 자체를 하지 않음)이라 중복 호출이 안전하다.
   */
  PARTICIPANT_SEAT_SEED: (id: string) =>
    `/api/meetings/${encodeURIComponent(id)}/participants/seat-seed`,
  /** 강제 미디어 처리 ACK (인증 대상 본인 — 재접속 stale 재적용 방지 조기 clear) */
  PARTICIPANT_FORCE_ACK: (id: string, identity: string) =>
    `/api/meetings/${encodeURIComponent(id)}/participants/${encodeURIComponent(identity)}/force-ack`,
  /** 제어 감사 로그 조회 (호스트 또는 ADMIN) */
  MEETING_CONTROL_AUDIT: (id: string) => `/api/meetings/${encodeURIComponent(id)}/control-audit`,
  /** [공개] 강제 미디어 ACK (게스트/공용 대상 조기 clear — roomName+identity 서버검증) */
  PUBLIC_FORCE_ACK: (id: string, identity: string) =>
    `/api/public/meetings/${encodeURIComponent(id)}/participants/${encodeURIComponent(identity)}/force-ack`,
  /** [공개] 세션 내 강제 거부(veto) 등록 — 대상 [끄기] */
  PUBLIC_MEDIA_VETO: (id: string, identity: string) =>
    `/api/public/meetings/${encodeURIComponent(id)}/participants/${encodeURIComponent(identity)}/media-veto`,
  /** [공개] OFF 마커 clear — 대상 자가 재활성 */
  PUBLIC_MUTED_CLEAR: (id: string, identity: string) =>
    `/api/public/meetings/${encodeURIComponent(id)}/participants/${encodeURIComponent(identity)}/muted-clear`,
  /** [공개] 접속 참가자 본인 손들기 — LiveKit 방/identity 토큰 재검증 */
  PUBLIC_RAISE_HAND: (id: string, identity: string) =>
    `/api/public/meetings/${encodeURIComponent(id)}/participants/${encodeURIComponent(identity)}/raise-hand`,
  /** 특정 참가자 강제 퇴장 (재입장 차단 옵션 포함) */
  PARTICIPANT_KICK: (id: string, identity: string) =>
    `/api/meetings/${encodeURIComponent(id)}/participants/${encodeURIComponent(identity)}/kick`,
  /** 특정 참가자의 권한 변경 (화면 공유 허용/차단) */
  PARTICIPANT_PERMISSIONS: (id: string, identity: string) =>
    `/api/meetings/${encodeURIComponent(id)}/participants/${encodeURIComponent(identity)}/permissions`,
  /** 특정 참가자 스포트라이트 설정/해제 */
  PARTICIPANT_SPOTLIGHT: (id: string, identity: string) =>
    `/api/meetings/${encodeURIComponent(id)}/participants/${encodeURIComponent(identity)}/spotlight`,
  /** 호스트 권한을 다른 참가자에게 이양 */
  TRANSFER_HOST: (id: string) => `/api/meetings/${encodeURIComponent(id)}/transfer-host`,
  /** 손들기 상태 토글 */
  RAISE_HAND: (id: string) => `/api/meetings/${encodeURIComponent(id)}/raise-hand`,
  /** 전체 레이아웃 강제(통일/고정): PUT=적용, DELETE=해제 */
  MEETING_LAYOUT: (id: string) => `/api/meetings/${encodeURIComponent(id)}/layout`,
  MEETING_SPEAKER: (id: string) => `/api/meetings/${encodeURIComponent(id)}/speaker`,
  MEETING_VIEWPORT_FOCUS: (id: string) => `/api/meetings/${encodeURIComponent(id)}/viewport-focus`,
  MEETING_CAMERA_MIRROR: (id: string) => `/api/meetings/${encodeURIComponent(id)}/camera-mirror`,
  /**
   * 슬롯 순서만 갱신(PUT). 레이아웃 모드를 건드리지 않아 **잠금을 만들지 않는다** —
   * 호스트 기준 순서 자동 동기화(`hooks/useSlotOrderAutoSync`)와 신규 입장자 재수렴이 쓴다.
   * (기존 `/layout`은 DTO의 mode가 `@NotBlank`라 order-only 전송이 구조적으로 불가하다.)
   */
  MEETING_SLOT_ORDER: (id: string) => `/api/meetings/${encodeURIComponent(id)}/slot-order`,
  MEETING_SINGLE_SPEAKER: (id: string, identity: string) =>
    `/api/meetings/${encodeURIComponent(id)}/single-speaker/${encodeURIComponent(identity)}`,
  MEETING_SINGLE_SPEAKER_CLEAR: (id: string) =>
    `/api/meetings/${encodeURIComponent(id)}/single-speaker`,
  MEETING_SINGLE_SPEAKER_CAPABILITY: (id: string) =>
    `/api/meetings/${encodeURIComponent(id)}/single-speaker/capability`,

  CHAT_UPLOAD_CLEANUP: (meetingId: string, storedName: string) =>
    `/api/chat/upload?meetingId=${encodeURIComponent(meetingId)}&storedName=${encodeURIComponent(storedName)}`,

  // ── 게스트 호스트 (공개 API — X-Guest-Host-Token 헤더 기반 인증) ──
  // JWT 없이 게스트 호스트 토큰만으로 참가자 제어가 가능한 공개 엔드포인트.
  // 게스트(비인증) 사용자가 호스트로 승격되었을 때 사용한다.
  // 토큰은 claim-token 엔드포인트에서 1회 발급받으며, 이후 모든 제어 API에 전달된다.

  /** 게스트 호스트 토큰 발급 (1회성 — identity 파라미터 필수) */
  GUEST_HOST_CLAIM_TOKEN: (id: string) =>
    `/api/public/meetings/${encodeURIComponent(id)}/host/claim-token`,
  /**
   * 임베드 Ghost 제어 토큰 교환.
   * LiveKit ghost 토큰을 `Authorization: Bearer`로 제시하면 회의 수명 제어 토큰을 돌려준다.
   * 임베드 iframe에는 로그인 세션이 없어 이 경로가 유일한 제어 인증 수단이다.
   */
  GHOST_CLAIM_CONTROL: (id: string) =>
    `/api/public/meetings/${encodeURIComponent(id)}/ghost/claim-control`,
  /** 게스트 호스트: 전체 참가자 일괄 음소거 */
  GUEST_HOST_MUTE_ALL: (id: string) =>
    `/api/public/meetings/${encodeURIComponent(id)}/host/participants/mute-all`,
  /** 게스트 호스트: 전체 참가자 카메라 끄기 */
  GUEST_HOST_CAMERA_OFF_ALL: (id: string) =>
    `/api/public/meetings/${encodeURIComponent(id)}/host/participants/camera-off-all`,
  /** 게스트 호스트: 특정 참가자 오디오/비디오 음소거 */
  GUEST_HOST_MUTE: (id: string, identity: string) =>
    `/api/public/meetings/${encodeURIComponent(id)}/host/participants/${encodeURIComponent(identity)}/mute`,
  /** 게스트 호스트: 특정 참가자 마이크/카메라 강제 켜기 (즉시 적용 — 대상 인지 배너/칩) */
  GUEST_HOST_FORCE_ENABLE: (id: string, identity: string) =>
    `/api/public/meetings/${encodeURIComponent(id)}/host/participants/${encodeURIComponent(identity)}/force-enable`,
  /** 게스트 호스트: 룸 전원 마이크/카메라 강제 켜기 (일반 참가자만 — self·다른 호스트 skip) */
  GUEST_HOST_FORCE_ENABLE_ALL: (id: string) =>
    `/api/public/meetings/${encodeURIComponent(id)}/host/participants/force-enable-all`,
  /** 게스트 호스트: 특정 참가자 강제 퇴장 */
  GUEST_HOST_KICK: (id: string, identity: string) =>
    `/api/public/meetings/${encodeURIComponent(id)}/host/participants/${encodeURIComponent(identity)}/kick`,
  /** 게스트 호스트: 특정 참가자 권한 변경 */
  GUEST_HOST_PERMISSIONS: (id: string, identity: string) =>
    `/api/public/meetings/${encodeURIComponent(id)}/host/participants/${encodeURIComponent(identity)}/permissions`,
  /** 게스트 호스트: 특정 참가자 스포트라이트 설정/해제 */
  GUEST_HOST_SPOTLIGHT: (id: string, identity: string) =>
    `/api/public/meetings/${encodeURIComponent(id)}/host/participants/${encodeURIComponent(identity)}/spotlight`,
  /** 게스트 호스트: 호스트 권한을 다른 참가자에게 이양 */
  GUEST_HOST_TRANSFER: (id: string) =>
    `/api/public/meetings/${encodeURIComponent(id)}/host/transfer-host`,
  /** 게스트 호스트: 회의 종료 (모든 참가자 연결 해제) */
  GUEST_HOST_END: (id: string) => `/api/public/meetings/${encodeURIComponent(id)}/host/end`,
  /** 게스트 호스트: 회의 나가기 (호스트 본인만 퇴장) */
  GUEST_HOST_LEAVE: (id: string) => `/api/public/meetings/${encodeURIComponent(id)}/host/leave`,
  /** 게스트 호스트: 손들기 상태 토글 */
  GUEST_HOST_RAISE_HAND: (id: string) =>
    `/api/public/meetings/${encodeURIComponent(id)}/host/raise-hand`,
  /** 게스트 호스트: 전체 레이아웃 강제(통일/고정) PUT=적용, DELETE=해제 */
  GUEST_HOST_LAYOUT: (id: string) => `/api/public/meetings/${encodeURIComponent(id)}/host/layout`,
  GUEST_HOST_SPEAKER: (id: string) => `/api/public/meetings/${encodeURIComponent(id)}/host/speaker`,
  GUEST_HOST_VIEWPORT_FOCUS: (id: string) =>
    `/api/public/meetings/${encodeURIComponent(id)}/host/viewport-focus`,
  GUEST_HOST_CAMERA_MIRROR: (id: string) =>
    `/api/public/meetings/${encodeURIComponent(id)}/host/camera-mirror`,
  /** 게스트 호스트: 슬롯 순서만 갱신(PUT) — 인증 경로 {@link MEETING_SLOT_ORDER}와 parity. */
  GUEST_HOST_SLOT_ORDER: (id: string) =>
    `/api/public/meetings/${encodeURIComponent(id)}/host/slot-order`,
  /** 게스트 호스트: 대기자 목록 조회(G-1) */
  GUEST_HOST_WAITING: (id: string) => `/api/public/meetings/${encodeURIComponent(id)}/host/waiting`,
  /** 게스트 호스트: 대기자 승인(G-1) */
  GUEST_HOST_ADMIT: (id: string, participantId: string) =>
    `/api/public/meetings/${encodeURIComponent(id)}/host/participants/${encodeURIComponent(participantId)}/admit`,
  /** 게스트 호스트: 대기자 거절(G-1) */
  GUEST_HOST_REJECT: (id: string, participantId: string) =>
    `/api/public/meetings/${encodeURIComponent(id)}/host/participants/${encodeURIComponent(participantId)}/reject`,
  /** 게스트 호스트: in-meeting 대기실 ON/OFF 토글(G-2, 안전 필드 한정) */
  GUEST_HOST_WAITING_ROOM: (id: string) =>
    `/api/public/meetings/${encodeURIComponent(id)}/host/waiting-room`,
  /** 게스트 호스트: 미참가(초대) 목록 조회(G-6) */
  GUEST_HOST_NON_PARTICIPANTS: (id: string) =>
    `/api/public/meetings/${encodeURIComponent(id)}/host/non-participants`,
  /** 게스트 호스트: 미참가자 강제 참가('연결', G-6) */
  GUEST_HOST_CONNECT: (id: string) =>
    `/api/public/meetings/${encodeURIComponent(id)}/host/participants/connect`,
  /** 게스트 호스트: 미참가 초대자 참가 요청(온디맨드 웹훅, G-6) */
  GUEST_HOST_REQUEST_JOIN: (id: string, inviteeId: string | number) =>
    `/api/public/meetings/${encodeURIComponent(id)}/host/participants/invitees/${encodeURIComponent(inviteeId)}/request-join`,
  /**
   * 게스트 호스트: 좌석 사전 배치 재-seed — 인증 경로 {@link PARTICIPANT_SEAT_SEED}와 parity.
   * 지정 게스트 호스트만 있는 회의(인증 호스트 부재)에서도 웹훅 미도달 시 좌석이 살아나야 한다.
   * (백엔드 매핑: `GuestHostController` 클래스 `/api/public/meetings/{id}/host` + `/participants/seat-seed`)
   */
  GUEST_HOST_SEAT_SEED: (id: string) =>
    `/api/public/meetings/${encodeURIComponent(id)}/host/participants/seat-seed`,

  // Meeting Thumbnails
  MEETING_THUMBNAILS: (id: string | number) => `/api/meetings/${encodeURIComponent(id)}/thumbnails`,
  MEETING_THUMBNAIL_REPRESENTATIVE: (id: string | number) =>
    `/api/meetings/${encodeURIComponent(id)}/thumbnails/representative`,
  MEETING_THUMBNAIL_DOWNLOAD: (id: string | number, thId: string | number) =>
    `/api/meetings/${encodeURIComponent(id)}/thumbnails/${encodeURIComponent(thId)}/download`,
  MEETING_THUMBNAIL_CAPTURE: (id: string | number) =>
    `/api/meetings/${encodeURIComponent(id)}/thumbnails/capture`,

  // Chat
  CHAT_UPLOAD: '/api/chat/upload',
  CHAT_FILE_PUBLIC: (fileName: string) => `/api/public/chat/files/${encodeURIComponent(fileName)}`,
  CHAT_MESSAGES: (meetingId: string) => `/api/chat/meetings/${meetingId}/messages`,

  // Meeting Records
  MEETING_RECORDS: '/api/meetings/records',
  MEETING_RECORDS_PAGED: '/api/meetings/records/paged',
  MEETING_RECORD_DETAIL: (id: string) => `/api/meetings/${id}/record-detail`,

  // Settings
  SETTINGS: '/api/settings',

  // User Devices
  USER_DEVICES_LATEST: '/api/user-devices/latest',
  USER_DEVICES_HISTORY: '/api/user-devices/history',
  USER_DEVICES: '/api/user-devices',

  // Notifications
  NOTIFICATIONS: '/api/notifications',
  NOTIFICATIONS_UNREAD_COUNT: '/api/notifications/unread-count',

  // Recordings
  RECORDINGS: '/api/recordings',
  RECORDINGS_BY_MEETING: (meetingId: string) => `/api/recordings/meeting/${meetingId}`,
  RECORDING_DOWNLOAD: (id: number) => `/api/recordings/${id}/download`,

  // LiveKit
  LIVEKIT_TOKEN: '/api/livekit/token',

  // Admin
  ADMIN_DASHBOARD: '/api/admin/dashboard',
  ADMIN_USERS: '/api/admin/users',
  ADMIN_USER_DETAIL: (id: number) => `/api/admin/users/${id}`,
  ADMIN_USERS_PENDING: '/api/admin/users/pending',
  ADMIN_USER_ROLE: (id: number) => `/api/admin/users/${id}/role`,
  ADMIN_USER_APPROVAL: (id: number) => `/api/admin/users/${id}/approval`,
  ADMIN_USER_SUSPEND: (id: number) => `/api/admin/users/${id}/suspend`,
  ADMIN_USER_UNSUSPEND: (id: number) => `/api/admin/users/${id}/unsuspend`,
  ADMIN_USER_RESET_PASSWORD: (id: number) => `/api/admin/users/${id}/reset-password`,
  ADMIN_USERS_BULK_APPROVE: '/api/admin/users/bulk/approve',
  ADMIN_USERS_BULK_REJECT: '/api/admin/users/bulk/reject',
  ADMIN_USERS_BULK_SUSPEND: '/api/admin/users/bulk/suspend',
  ADMIN_USERS_BULK_UNSUSPEND: '/api/admin/users/bulk/unsuspend',
  ADMIN_POLICIES: '/api/admin/policies',
  ADMIN_EMAIL_TEST: '/api/admin/email/test',
  ADMIN_POLICY: (key: string) => `/api/admin/policies/${key}`,
  // Admin Recordings
  ADMIN_RECORDINGS: '/api/admin/recordings',
  ADMIN_RECORDING_DOWNLOAD: (id: number) => `/api/admin/recordings/${id}/download`,
  ADMIN_RECORDING_DELETE: (id: number) => `/api/admin/recordings/${id}`,
  ADMIN_RECORDINGS_SYNC: '/api/admin/recordings/sync',

  // User Tokens
  MY_TOKEN: '/api/users/me/tokens',
  MY_TOKEN_REGENERATE: '/api/users/me/tokens/regenerate',
  MY_TOKEN_ALLOWED_URL: '/api/users/me/tokens/allowed-url',

  // Notices
  NOTICES: '/api/notices',
  NOTICE_BY_ID: (id: number) => `/api/notices/${id}`,
  NOTICE_FILE_DOWNLOAD: (noticeId: number, fileId: number) =>
    `/api/notices/${noticeId}/files/${fileId}/download`,
  NOTICE_FILE_DELETE: (noticeId: number, fileId: number) =>
    `/api/notices/${noticeId}/files/${fileId}`,
  NOTICE_UPLOAD_IMAGE: '/api/notices/upload-image',

  // Common Codes
  CODES: '/api/codes',
} as const;

// 백엔드 ErrorCode enum과 일치하는 에러 코드 상수
export const ERROR_CODES = {
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  EMAIL_ALREADY_EXISTS: 'EMAIL_ALREADY_EXISTS',
  USERNAME_ALREADY_EXISTS: 'USERNAME_ALREADY_EXISTS',
  SIGNUP_IDENTIFIER_REQUIRED: 'SIGNUP_IDENTIFIER_REQUIRED',
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  TOKEN_INVALID: 'TOKEN_INVALID',
  MEETING_NOT_FOUND: 'MEETING_NOT_FOUND',
  MEETING_PASSWORD_REQUIRED: 'MEETING_PASSWORD_REQUIRED',
  MEETING_PASSWORD_INVALID: 'MEETING_PASSWORD_INVALID',
  MEETING_FULL: 'MEETING_FULL',
  WAITING_ROOM_PENDING: 'WAITING_ROOM_PENDING',
  WAITING_ROOM_REJECTED: 'WAITING_ROOM_REJECTED',
  MEETING_ALREADY_ENDED: 'MEETING_ALREADY_ENDED',
  MEETING_NOT_YET_OPEN: 'MEETING_NOT_YET_OPEN',
  RATE_LIMITED: 'RATE_LIMITED',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
} as const;
